import PersonIcon from "@mui/icons-material/Person";
import EngineeringIcon from "@mui/icons-material/Engineering";
import StorageIcon from "@mui/icons-material/Storage";
import FeedIcon from "@mui/icons-material/Feed";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import PeopleIcon from "@mui/icons-material/People";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CookieIcon from "@mui/icons-material/Cookie";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import InfoIcon from "@mui/icons-material/Info";
import BarChartIcon from "@mui/icons-material/BarChart";
import ChecklistIcon from "@mui/icons-material/Checklist";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import LyricsIcon from "@mui/icons-material/Lyrics";
import TranslateIcon from "@mui/icons-material/Translate";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";

import { isAdmin, isReviewer } from "../../helper-functions/shared/miscUtils";

import { getLanguage } from "../language/language";

const LANG = getLanguage();

// Runtime overrides: if `public/env-config.js` (served statically) defines `window._env_`, prefer those values.
// This file can be edited or mounted in production without rebuilding the frontend bundle.
const RUNTIME_ENV = typeof window !== "undefined" && window._env_ ? window._env_ : {};

// // // // //
// GENERAL
// Misc. constants
// // // // //
export const VERSION = RUNTIME_ENV.VERSION || "v0.5.1";
export const INDEX_PATH = ""; // Web server's index.html path.
export const PAGE_ENABLE_LOADER = true; // Show spinner animation.
export const PAGE_LOADING_MS = 1200; // Page spinner.

export const IFRAME_TIMEOUT_MS = 7500; // Show an error message if the iframe is not loaded after this time.
export const HTTP_QUERY_TIMEOUT_MS = 60000; // Timeout for HTTP queries.

export const FRONTEND_ONLY = false; // Used in changing the API paths to static local JSON files.

// NB! These are invalid tokens, but fit our debug purposes well.
export const DEBUG_ADMIN_JWT =
    "eyJhbGciOiJIUzUxMiJ9.eyJyb2xlIjoiUk9MRV9BRE1JTiIsInVzZXJuYW1lIjoiYWRtaW4ifQ.NxCPtUwVPmayPbiokxxUwjCuNZt--bwK8dAz96-7UeoNPUIdIm6Evk36rfypnOLyh-B_9pS7eWRgHR_sLHS0lA";
export const DEBUG_USER_JWT =
    "eyJhbGciOiJIUzUxMiJ9.eyJyb2xlIjoiUk9MRV9VU0VSIiwidXNlcm5hbWUiOiJ1c2VyIn0.D3K0mZDOnkFc0wvNRgklUyVulJTjdM0af-Y0dT1dp6W3QtrnrX3lCEur_Rm0XxiaVboUAGNF68IQ8fKhAPi9tw";

// // // // //
// PAGE ELEMENTS
// Toolbar, drawer, and other HTML elements.
// Also includes pagination.
// // // // //
export const AUTO_OPEN_SIDEBAR_PX = 1200;
export const HIDE_TOOLBAR_DESCRIPTION_PX = 1015;
export const PAGE_DRAWER_WIDTH_PX = 275;
export const ELEMENT_MIN_WIDTH_PX = 285;
export const SIDEBAR_TEXT_MIN_WIDTH = 24;

export const TABLE_ROWS_PER_PAGE = [5, 10, 25, 50];
export const DEFAULT_TABLE_ROWS_PER_PAGE = 10;

export const TABLE_LOADING_MS = 2500; // Show loading widget before deciding whether to show the table.
export const TABLE_HEIGHT_DESKTOP_PX = 420;
export const TABLE_HEIGHT_MOBILE_PX = 325;
export const TABLE_ROW_HEIGHT_DESKTOP_PX = 50;
export const TABLE_ROW_HEIGHT_MOBILE_PX = 30;

// // // // //
// SERVER
// Network parameters
// // // // //
export const DEVELOPMENT_ENV = ["localhost", "127.0.0.1"].includes(window.location.hostname) && window.location.port === "3000";

const DEFAULT_SERVER_IP = window.location.protocol + "//" + window.location.hostname; // E.g. http://193.40.255.27
const DEFAULT_SERVER_PORT = DEVELOPMENT_ENV ? ":8080" : "";

export const SERVER_IP = DEVELOPMENT_ENV ? DEFAULT_SERVER_IP : RUNTIME_ENV.SERVER_IP || DEFAULT_SERVER_IP;
export const SERVER_PORT = DEVELOPMENT_ENV ? DEFAULT_SERVER_PORT : RUNTIME_ENV.SERVER_PORT || DEFAULT_SERVER_PORT;

// // // // //
// API PATHS
// API endpoints to query data from the API.
// // // // //
export const API_JWT_VALIDATION = "/api/validate";
export const API_LOGIN_PATH = "/api/signin";
export const API_SIGNUP_PATH = "/api/signup";

export const API_PROGRAM_STATUS = "/api/status";

export const API_STATISTICS_RESOURCES = "/api/statistics/resources";
export const API_STATISTICS_TRANSCRIPTS = "/api/statistics/transcripts";
export const API_STATISTICS_AUTHORS = "/api/statistics/authors";
export const API_STATISTICS_HIGHLIGHTS = "/api/statistics/highlights";

export const API_LIBRARIES = "/api/libraries";
export const API_LIBRARIES_ID = "/api/libraries/<library_id>";
export const API_LIBRARIES_ID_APPROVE = "/api/libraries/<library_id>/approve";
export const API_LIBRARIES_ID_DISAPPROVE = "/api/libraries/<library_id>/disapprove";
export const API_LIBRARIES_ID_RETRY = "/api/libraries/<library_id>/retry";
export const API_LIBRARIES_APPROVE = "/api/libraries/approve";
export const API_LIBRARIES_DISAPPROVE = "/api/libraries/disapprove";
export const API_LIBRARIES_RETRY = "/api/libraries/retry";
export const API_LIBRARIES_ID_RETRY_METADATA = "/api/libraries/<library_id>/retry-metadata";
export const API_LIBRARIES_START_PROCESS = "/api/libraries/start-queued-process";
export const API_LIBRARIES_STOP_PROCESS = "/api/libraries/stop-queued-process";
export const API_LIBRARIES_SEND_WEBHOOK = "/api/libraries/send-webhook";
export const API_LIBRARIES_ID_RECHECK = "/api/libraries/<library_id>/recheck-catalog";

export const API_PLAYLISTS = "/api/playlists";
export const API_PLAYLISTS_ID = "/api/playlists/<playlist_id>";
export const API_PLAYLISTS_ID_RESOURCES = "/api/playlists/<playlist_id>/resources";

export const API_RESOURCES = "/api/resources";
export const API_RESOURCES_UPLOAD = "/api/resources/upload";
export const API_RESOURCES_APPROVE = "/api/resources/approve";
export const API_RESOURCES_RETRY = "/api/resources/retry";
export const API_RESOURCES_RETRY_METADATA = "/api/resources/retry-metadata";
export const API_RESOURCES_ID = "/api/resources/<resource_id>";
export const API_RESOURCES_ID_APPROVE = "/api/resources/<resource_id>/approve";
export const API_RESOURCES_ID_RETRY = "/api/resources/<resource_id>/retry";
export const API_RESOURCES_ID_RETRY_METADATA = "/api/resources/<resource_id>/retry-metadata";

export const API_TRANSCRIPTS = "/api/transcripts";
export const API_TRANSCRIPTS_APPROVE = "/api/transcripts/approve";
export const API_TRANSCRIPTS_RETRY = "/api/transcripts/retry";
export const API_TRANSCRIPTS_RETRY_CLEANUP = "/api/transcripts/retry-cleanup";
export const API_TRANSCRIPTS_ID = "/api/transcripts/<transcript_id>";
export const API_TRANSCRIPTS_ID_APPROVE = "/api/transcripts/<transcript_id>/approve";
export const API_TRANSCRIPTS_ID_RETRY = "/api/transcripts/<transcript_id>/retry";
export const API_TRANSCRIPTS_ID_RETRY_CLEANUP = "/api/transcripts/<transcript_id>/retry-cleanup";

export const API_QUOTES = "/api/quotes";
export const API_QUOTES_ID = "/api/quotes/<quote_id>";
export const API_TRANSCRIPT_ID_QUOTES = "/api/transcripts/<transcript_id>/quotes";

export const API_TEXT_TRANSLATIONS = "/api/translations/text";
export const API_TEXT_TRANSLATIONS_ID = "/api/translations/text/<translation_id>";
export const API_TEXT_TRANSLATIONS_APPROVE = "/api/translations/text/approve";
export const API_TEXT_TRANSLATIONS_RETRY = "/api/translations/text/retry";

export const API_AUDIO_TRANSLATIONS = "/api/translations/audio";
export const API_AUDIO_TRANSLATION_ID = "/api/translations/audio/<audio_id>";
export const API_AUDIO_TRANSLATIONS_APPROVE = "/api/translations/audio/approve";
export const API_AUDIO_TRANSLATIONS_RETRY = "/api/translations/audio/retry";
export const API_AUDIO_FILE = "/api/translations/audio/<audio_id>/player";

export const API_AUTHORS = "/api/authors";
export const API_AUTHORS_ID = "/api/authors/<author_id>";
export const API_AUTHORS_APPLY_EXCLUSIONS = "/api/authors/apply-exclusions";
export const API_AUTHORS_ID_APPLY_EXCLUSIONS = "/api/authors/<author_id>/apply-exclusions";
export const API_AUTHORS_APPLY_ALIASES = "/api/authors/apply-aliases";
export const API_AUTHORS_ID_APPLY_ALIASES = "/api/authors/<author_id>/apply-aliases";

export const API_SEARCH_KEYWORD = "/api/search/keyword";
export const API_SEARCH_SEMANTIC = "/api/search/semantic";

export const API_COOKIES = "/api/cookies";
export const API_COOKIES_LATEST = "/api/cookies/latest";
export const API_COOKIES_ID = "/api/cookies/<cookie_id>";

export const API_USERS = "/api/users";
export const API_USERS_ID = "/api/users/<user_id>";
export const API_USERS_ME = "/api/users/me";

export const API_HIGHLIGHT_REQUESTS = "/api/highlights/requests";
export const API_HIGHLIGHT_REQUESTS_APPROVE = "/api/highlights/requests/approve";
export const API_HIGHLIGHT_REQUESTS_DISAPPROVE = "/api/highlights/requests/disapprove";
export const API_HIGHLIGHT_REQUESTS_RETRY = "/api/highlights/requests/retry";
export const API_HIGHLIGHTS_REQUEST_ID = "/api/highlights/requests/<request_id>";
export const API_HIGHLIGHT_REQUESTS_START_PROCESS = "/api/highlights/requests/start-queued-process";
export const API_HIGHLIGHT_REQUESTS_STOP_PROCESS = "/api/highlights/requests/stop-queued-process";

export const API_HIGHLIGHT_REVIEWS = "/api/highlights/reviews";
export const API_HIGHLIGHTS_REVIEW_ID = "/api/highlights/reviews/<review_id>";

export const API_HIGHLIGHTS = "/api/highlights";
export const API_HIGHLIGHTS_REQUIRES_REVIEW = "/api/highlights/requires-review";
export const API_HIGHLIGHTS_APPROVE = "/api/highlights/approve";
export const API_HIGHLIGHTS_DISAPPROVE = "/api/highlights/disapprove";
export const API_HIGHLIGHTS_COMPLETE = "/api/highlights/complete";
export const API_HIGHLIGHT_ID = "/api/highlights/<highlight_id>";

// AUTH AND PRIVILEGES
export const ROLE_USER = "USER";
export const ROLE_ADMIN = "ADMIN";
export const ROLE_SYSTEM = "SYSTEM";
export const ROLE_LEVEL_1_REVIEWER = "LEVEL_1_REVIEWER";
export const ROLE_LEVEL_2_REVIEWER = "LEVEL_2_REVIEWER";

// // // // //
// OBJECT STRUCTURES
// Define the basic structure of objects.
// NB! All configuration 'form' elements are automatically added during parsing.
// // // // //

// The structure of a configuration form field, describing whether it has any errors.
export const FORM_ELEMENT = {
    error: false,
    helperText: ""
};

export const COOKIE_TYPES = ["YOUTUBE.COM", "VIMEO.COM", "BUNNY.NET"];

export const RESOURCE_TYPES = ["LECTURE", "PODCAST", "SHORT", "INTERVIEW", "DEBATE", "UNCATEGORIZED"];

export const USER_STATUS_TYPES = ["ACTIVE", "INACTIVE", "REMOVED"];

export const HIGHLIGHT_REVIEW_VOTE_TYPES = ["YES", "MAYBE", "NO"];

export const ROLE_TYPES = [
    { id: 5, role: ROLE_SYSTEM },
    { id: 2, role: ROLE_USER },
    { id: 3, role: ROLE_LEVEL_1_REVIEWER },
    { id: 4, role: ROLE_LEVEL_2_REVIEWER },
    { id: 1, role: ROLE_ADMIN }
];

export const LIBRARY_REQUEST_OBJECT = {
    name: "",
    description: "",
    url: ""
};

export const RESOURCE_REQUEST_OBJECT = {
    name: "",
    description: "",
    url: "",
    meta_data: {},

    uploaded_on: "",
    type: "",

    // Temporary fields.
    _file_name: "",
    _referer_url: "",

    // FK relationships
    author_ids: []
};

export const TRANSCRIPT_REQUEST_OBJECT = {
    text: "",
    meta_data: []
};

export const TRANSCRIPT_QUOTE_OBJECT = {
    text: "",
    meta_data: []
};

export const AUTHOR_REQUEST_OBJECT = {
    first_name: "",
    last_name: "",
    bio: "",
    aliases: "",
    exclusions: ""
};

export const TEXT_TRANSLATION_OBJECT = {
    resource_id: "",
    text: "",
    language: "",
    meta_data: {},

    // Temporary fields.
    _instructions: "",
    _show_accordion: false
};

export const AUDIO_TRANSLATION_OBJECT = {
    translation_id: "",
    meta_data: {},

    // Temporary fields.
    _voice: "",
    _instructions: ""
};

export const COOKIE_REQUEST_OBJECT = {
    text: "",
    type: "",
    note: "",
    manual_use: false
};

export const USER_REQUEST_OBJECT = {
    username: "",
    email: "",
    role_id: "",
    status: "",

    // Temporary fields.
    _role: ""
};

export const HIGHLIGHT_REQUEST_OBJECT = {
    name: "",
    description: "",
    constraints: {
        library_ids: [],
        resource_ids: [],
        author_ids: [],

        max_length_secs: 120,
        min_timestamp_secs: 180,

        recurring_process: true,

        diversity_weight: 0.1,
        threshold: 0.15
    },

    // Temporary fields.
    _max_length_secs: "",
    _min_timestamp_secs: "",
    _recurring_process: true,
    _diversity_weight: "",
    _threshold: "",
    _show_accordion: false
};

export const HIGHLIGHT_REQUEST_STATUSES = ["AWAITING_APPROVAL", "IN_QUEUE", "IN_PROGRESS", "COMPLETED", "FAILED"];

export const DEFAULT_TRANSLATE_INSTRUCTIONS = `Voice: Clear and expressive, with occasional thought pauses and drawn-out words.
<accent>
Tone: Informative and casual, maintaining a balance between formality and approachability.

Punctuation: Structured with commas and pauses for clarity, ensuring information is digestible and well-paced.

Delivery: As a professional podcaster, emphasizing the key ideas, quotes, and numbers to highlight critical points.`;

export const TRANSLATE_LANGUAGES = [
    "English (en)",
    "French (fr)",
    "German (de)",

    "Italian (it)",
    "Portuguese (pt)",
    "Spanish (es)",

    "Swedish (sv)",
    "Norwegian (no)",
    "Danish (da)",
    "Finnish (fi)",

    "Estonian (et)",
    "Latvian (lv)",
    "Lithuanian (lt)",

    "Russian (ru)",
    "Polish (pl)",
    "Czech (cs)",
    "Slovak (sk)",
    "Hungarian (hu)",
    "Romanian (ro)",

    "Greek (el)",
    "Turkish (tr)",
    "Arabic (ar)",
    "Hebrew (he)",

    "Thai (th)",
    "Vietnamese (vi)",
    "Indonesian (id)",
    "Chinese (zh)",
    "Japanese (ja)",
    "Korean (ko)"
];

export const VOICE_OPTIONS = ["alloy", "ash", "ballad", "cedar", "coral", "echo", "fable", "nova", "onyx", "shimmer"];

export const HIGHLIGHT_REQUEST_REVIEW_FILTER_OBJECT = {
    highlight_request_ids: [],

    // Temporary fields.
    _show_accordion: false
};

export const HIGHLIGHT_REVIEW_REQUEST_OBJECT = {
    highlight_id: "",
    vote: "",
    note: ""
};

// Dummy object for displaying the system's the highlight review response.
export const HIGHLIGHT_REVIEW_RESPONSE_OBJECT = {
    id: -1,
    vote: null,
    note: null,
    created_on: "",
    user: {
        email: "FRISCO",
        role: {
            role: "SYSTEM"
        }
    }
};

// // // // //
// TOUR STEPS
// Used in the tour guides, describing the app's features.
// // // // //
export const TOUR_GUIDE_STEPS = {
    SEARCH: {
        paths: ["/home"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_SEARCH_1}</span>,
                placement: "center"
            },
            {
                target: ".tour-search-2",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_SEARCH_2}</span>
            },
            {
                target: ".tour-search-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_SEARCH_3}</span>
            },
            {
                target: ".tour-search-4",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_SEARCH_4}</span>
            }
        ]
    },
    ABOUT: {
        paths: ["/about"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_1}</span>,
                placement: "center"
            },
            {
                target: ".tour-about-2",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_2}</span>
            },
            {
                target: ".tour-about-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_3}</span>
            },
            {
                target: ".tour-about-4",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_4}</span>
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_5}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_6}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_7}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_ABOUT_8}</span>,
                placement: "center"
            }
        ]
    },
    STATISTICS: {
        paths: ["/database/statistics"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_STATISTICS_1}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_STATISTICS_2}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_STATISTICS_3}</span>,
                placement: "center"
            }
        ]
    },
    LIBRARIES: {
        paths: ["/database/libraries"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_LIBRARIES_1}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_LIBRARIES_2}</span>,
                placement: "center"
            },
            {
                target: ".tour-libraries-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_LIBRARIES_3}</span>
            },
            {
                target: ".tour-libraries-4",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_LIBRARIES_4}</span>
            },
            {
                target: ".tour-libraries-5",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_LIBRARIES_5}</span>
            },
            {
                target: ".tour-libraries-6",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_LIBRARIES_6}</span>
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_LIBRARIES_7}</span>,
                placement: "center"
            }
        ]
    },
    PLAYLISTS: {
        paths: ["/database/playlists"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_PLAYLISTS_1}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_PLAYLISTS_2}</span>,
                placement: "center"
            },
            {
                target: ".tour-playlists-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_PLAYLISTS_3}</span>
            },
            {
                target: ".tour-playlists-4",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_PLAYLISTS_4}</span>
            }
        ]
    },
    RESOURCES: {
        paths: ["/database/resources"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_RESOURCES_1}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_RESOURCES_2}</span>,
                placement: "center"
            },
            {
                target: ".tour-resources-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_RESOURCES_3}</span>
            },
            {
                target: ".tour-resources-4",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_RESOURCES_4}</span>
            },
            {
                target: ".tour-resources-5",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_RESOURCES_5}</span>
            },
            {
                target: ".tour-resources-6",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_RESOURCES_6}</span>
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_RESOURCES_7}</span>,
                placement: "center"
            }
        ]
    },
    TRANSCRIPTS: {
        paths: ["/database/transcripts"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TRANSCRIPTS_1}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TRANSCRIPTS_2}</span>,
                placement: "center"
            },
            {
                target: ".tour-transcripts-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TRANSCRIPTS_3}</span>
            },
            {
                target: ".tour-transcripts-4",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TRANSCRIPTS_4}</span>
            },
            {
                target: ".tour-transcripts-5",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TRANSCRIPTS_5}</span>
            },
            {
                target: ".tour-transcripts-6",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TRANSCRIPTS_6}</span>
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TRANSCRIPTS_7}</span>,
                placement: "center"
            }
        ]
    },
    QUOTES: {
        paths: ["/database/quotes"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_QUOTES_1}</span>,
                placement: "center"
            },
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_QUOTES_2}</span>,
                placement: "center"
            },
            {
                target: ".tour-quotes-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_QUOTES_3}</span>
            },
            {
                target: ".tour-quotes-4",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_QUOTES_4}</span>
            }
        ]
    },
    AUTHORS: {
        paths: ["/database/authors"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_AUTHORS_1}</span>,
                placement: "center"
            },
            {
                target: ".tour-authors-2",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_AUTHORS_2}</span>
            },
            {
                target: ".tour-authors-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_AUTHORS_3}</span>
            }
        ]
    },
    TEXT_TRANSLATION: {
        paths: ["/translations/text"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TEXT_TRANSLATIONS_1}</span>,
                placement: "center"
            },
            {
                target: ".tour-text-translations-2",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TEXT_TRANSLATIONS_2}</span>
            },
            {
                target: ".tour-text-translations-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_TEXT_TRANSLATIONS_3}</span>
            }
        ]
    },
    AUDIO_TRANSLATION: {
        paths: ["/translations/audio"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_AUDIO_TRANSLATIONS_1}</span>,
                placement: "center"
            },
            {
                target: ".tour-audio-translations-2",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_AUDIO_TRANSLATIONS_2}</span>
            },
            {
                target: ".tour-audio-translations-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_AUDIO_TRANSLATIONS_3}</span>
            }
        ]
    },
    HIGHLIGHT_REQUESTS: {
        paths: ["/highlights/requests"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_HIGHLIGHT_REQUESTS_1}</span>,
                placement: "center"
            },
            {
                target: ".tour-highlight-requests-2",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_HIGHLIGHT_REQUESTS_2}</span>
            },
            {
                target: ".tour-highlight-requests-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_HIGHLIGHT_REQUESTS_3}</span>
            }
        ]
    },
    HIGHLIGHT_REVIEW: {
        paths: ["/highlights/view"],
        steps: [
            {
                target: "body",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_HIGHLIGHT_REVIEWS_1}</span>,
                placement: "center"
            },
            {
                target: ".tour-highlight-reviews-2",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_HIGHLIGHT_REVIEWS_2}</span>
            },
            {
                target: ".tour-highlight-reviews-3",
                content: <span style={{ whiteSpace: "pre-line" }}>{LANG.TOUR_HIGHLIGHT_REVIEWS_3}</span>
            }
        ]
    }
};

// // // // //
// CATEGORIES
// Used in sidebar links.
// // // // //
export const DATABASE_METRICS = {
    id: "DATABASE_METRICS",
    name: "Metrics",
    description: "View quantitative information about the system",
    path: "/database",
    icon: <ViewQuiltIcon />,
    categories: {
        statistics: {
            id: "STATISTICS",
            path: "/statistics",
            name: "Statistics",
            description: "View statistics about database contents",
            visible: true,
            icon: <BarChartIcon />
        }
    }
};

export const DATABASE_TABLES = {
    id: "DATABASE_TABLES",
    name: "Database",
    description: "View information and perform detailed queries on various database tables.",
    path: "/database",
    icon: <StorageIcon />,
    categories: {
        libraries: {
            id: "LIBRARIES",
            path: "/libraries",
            name: "Libraries",
            description: "View and manage libraries",
            visible: true,
            icon: <SubscriptionsIcon />
        },
        playlists: {
            id: "PLAYLISTS",
            path: "/playlists",
            name: "Playlists",
            description: "View and manage playlists",
            visible: true,
            icon: <PlaylistPlayIcon />
        },
        resources: {
            id: "RESOURCES",
            path: "/resources",
            name: "Resources",
            description: "View and manage resources",
            visible: true,
            icon: <FeedIcon />
        },
        transcripts: {
            id: "TRANSCRIPTS",
            path: "/transcripts",
            name: "Transcripts",
            description: "View and manage transcripts",
            visible: true,
            icon: <TextFieldsIcon />
        },
        quotes: {
            id: "QUOTES",
            path: "/quotes",
            name: "Quotes",
            description: "View and manage quotes",
            visible: true,
            icon: <FormatQuoteIcon />
        },
        authors: {
            id: "AUTHORS",
            path: "/authors",
            name: "Authors",
            description: "View and manage authors",
            visible: true,
            icon: <PersonIcon />
        }
    }
};

export const TRANSLATION_TABLES = {
    id: "TRANSLATION_TABLES",
    name: "Translations",
    description: "View and manage translations of resources.",
    path: "/translations",
    icon: <LyricsIcon />,
    visible: (props) => props.browser?.showTranslations,
    categories: {
        translations: {
            id: "TRANSLATIONS",
            path: "/text",
            name: "Translated text",
            description: "View and manage translations of transcripts",
            visible: true,
            icon: <TranslateIcon />
        },
        audio: {
            id: "AUDIO",
            path: "/audio",
            name: "Translated audio",
            description: "View and manage audio files created from translations",
            visible: true,
            icon: <AudiotrackIcon />
        }
    }
};

export const HIGHLIGHT_TABLES = {
    id: "HIGHLIGHT_TABLES",
    name: "Highlights",
    description: "Create highlight requests and review highlights created by the system.",
    path: "/highlights",
    visible: (props) => isAdmin(props) || isReviewer(props),
    icon: <ChecklistIcon />,
    categories: {
        request: {
            id: "HIGHLIGHT_REQUESTS",
            path: "/requests",
            name: "Requests",
            description: "Create and manage highlight requests",
            visible: true,
            icon: <LightbulbIcon />
        },
        highlights: {
            id: "HIGHLIGHTS",
            path: "/view",
            name: "View and review",
            description: "View highlights created by the system, and review them to approve or disapprove them.",
            visible: true,
            icon: <MovieFilterIcon />
        }
    }
};

export const DROPDOWN_CATEGORIES = [DATABASE_METRICS, DATABASE_TABLES, TRANSLATION_TABLES, HIGHLIGHT_TABLES];

export const ADMINISTRATION = {
    id: "ADMINISTRATION",
    name: "Administration",
    description: "Manage administrative tasks like user accounts, roles, and permissions.",
    path: "/admin",
    icon: <EngineeringIcon />,
    categories: {
        about: {
            id: "ABOUT",
            path: "/about",
            name: "About",
            description: "About the system",
            visible: false,
            icon: <InfoIcon />
        },
        account: {
            id: "ACCOUNT",
            path: "/account",
            name: "Account",
            description: "Manage your account",
            visible: true,
            icon: <AccountCircleIcon />
        },
        users: {
            id: "USERS",
            path: "/users",
            name: "Users",
            description: "Manage other users' accounts",
            visible: (props) => isAdmin(props),
            icon: <PeopleIcon />
        },
        cookies: {
            id: "COOKIES",
            path: "/cookies",
            name: "Cookies",
            description: "Manage cookies for YouTube authentication",
            visible: (props) => isAdmin(props),
            icon: <CookieIcon />
        }
    }
};
